import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MapPin, Users, Wifi, Waves, Car, Music, Star } from 'lucide-react';

const AMENITY_ICONS = {
  wifi: <Wifi size={12} />, pool: <Waves size={12} />,
  parking: <Car size={12} />, music: <Music size={12} />,
};

const TYPE_LABELS = {
  homestay: 'Homestay', hotel: 'Hotel', pg: 'PG',
  hostel: 'Hostel', farmhouse: 'Farmhouse', penthouse: 'Penthouse',
  function_hall: 'Function Hall', banquet_hall: 'Banquet Hall'
};

const PropertyCard = ({ property, className = '', index = 0 }) => {
  const image = property.images?.[0] || 'https://images.unsplash.com/photo-1607567618395-62fc2d132c3e?w=600';
  const avgRating = property.avg_rating;
  const reviewCount = property.review_count;

  return (
    <motion.div
      data-testid="property-card"
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.07, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
      whileHover={{ y: -5, transition: { duration: 0.2 } }}
      className={`group bg-white border border-gray-100 rounded-2xl overflow-hidden cursor-pointer ${className}`}
      style={{ boxShadow: '0 1px 3px rgba(10,22,40,0.06)' }}
    >
      <Link to={`/property/${property.id}`}>
        <div className="relative overflow-hidden h-52">
          <img src={image} alt={property.name}
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
          <div className="absolute inset-0 bg-gradient-to-t from-navy/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
          <div className="absolute top-3 left-3 flex gap-1.5">
            <span className="bg-white/95 text-navy text-xs font-semibold px-2.5 py-1 rounded-full">
              {TYPE_LABELS[property.type] || property.type}
            </span>
          </div>
          {avgRating > 0 && (
            <div className="absolute top-3 right-3 bg-white/95 text-navy text-xs font-semibold px-2 py-1 rounded-full flex items-center gap-1">
              <Star size={10} fill="#C9A84C" className="text-gold" strokeWidth={0} />
              {avgRating.toFixed(1)}
            </div>
          )}
          <div className="absolute bottom-3 right-3 flex gap-1">
            {property.experiences?.map(exp => (
              <span key={exp} className="bg-navy/80 text-white text-xs px-2 py-0.5 rounded-full capitalize backdrop-blur-sm">{exp}</span>
            ))}
          </div>
        </div>

        <div className="p-4">
          <div className="flex items-start justify-between gap-2 mb-1.5">
            <h3 className="font-semibold text-navy text-[15px] leading-snug line-clamp-1">{property.name}</h3>
            <span className="text-gold font-bold text-sm shrink-0">₹{property.price_min?.toLocaleString('en-IN')}+</span>
          </div>
          <div className="flex items-center gap-1.5 text-gray-400 text-xs mb-3">
            <MapPin size={11} className="shrink-0" />
            <span className="truncate">{property.location?.area ? `${property.location.area}, ` : ''}{property.location?.city}</span>
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1.5">
              {property.amenities?.slice(0, 3).map(a => AMENITY_ICONS[a] ? (
                <span key={a} className="text-gray-400" title={a}>{AMENITY_ICONS[a]}</span>
              ) : null)}
              {property.amenities?.length > 3 && <span className="text-gray-400 text-xs">+{property.amenities.length - 3}</span>}
            </div>
            {reviewCount > 0 ? (
              <div className="flex items-center gap-1 text-xs text-gray-500">
                <Star size={10} fill="#C9A84C" className="text-gold" strokeWidth={0} />
                <span>{avgRating?.toFixed(1)} ({reviewCount})</span>
              </div>
            ) : (
              <div className="flex items-center gap-1 text-gray-400 text-xs">
                <Users size={11} /><span>Up to {property.max_guests}</span>
              </div>
            )}
          </div>
        </div>
      </Link>
    </motion.div>
  );
};

export default PropertyCard;
